// Reset System Actions Confirmation Dialog (WP-07).
import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { systemActionReset } from '../../lib/bots';
import { Button } from '../ui/Button';
import { CloseIcon, AlertTriangleIcon } from '../icons';
import { useToast } from '../ui/Toast';

interface ResetActionsConfirmDialogProps {
  open: boolean;
  integrationId: string;
  onClose: () => void;
  onReset: () => void;
}

export default function ResetActionsConfirmDialog({
  open,
  integrationId,
  onClose,
  onReset,
}: ResetActionsConfirmDialogProps) {
  const { t } = useI18n();
  const toast = useToast();
  const [resetting, setResetting] = useState(false);

  if (!open) return null;

  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await systemActionReset({ integrationId });
      if (res.data?.ok) {
        toast.success(t('bots.actions.resetSuccess') || 'All system actions reset to defaults');
        onReset();
      }
    } catch (err) {
      console.error('[ResetActionsConfirmDialog] reset error', err);
      toast.error((err as Error).message || 'Failed to reset system actions');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="reset-actions-dialog-title"
        className="relative w-full max-w-md rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden"
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div className="flex items-center gap-2.5 text-danger">
            <AlertTriangleIcon className="h-5 w-5" />
            <h3 id="reset-actions-dialog-title" className="text-sm font-bold text-ink dark:text-white">
              {t('bots.actions.resetTitle') || 'Reset System Actions'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="rounded-lg p-1 text-ink-3 hover:bg-surface dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 text-xs text-ink-2 dark:text-slate-300 space-y-3">
          <p>
            {t('bots.actions.resetWarning') ||
              'Are you sure you want to reset all 9 system action buttons to factory defaults? Custom reply texts, assigned templates, and flow triggers will be overwritten.'}
          </p>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300 text-[11px]">
            {t('bots.actions.resetNote') ||
              'This action takes effect immediately across all automated replies for this WhatsApp number.'}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-3 dark:border-slate-800">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={resetting}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="danger" size="sm" onClick={handleReset} loading={resetting}>
            {t('bots.actions.confirmReset') || 'Reset to Defaults'}
          </Button>
        </div>
      </div>
    </div>
  );
}
